import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
} from "@mui/material";
import { MyFormData } from "./form-context";
import Toast from "@/components/toast";
import CloseIcon from "@mui/icons-material/Close";
import StepInputsForm from "./ptwStepInputsForm";
import WorkPermitSummary from "@/components/WorkPermitSummary";
import { useTranslation } from "react-i18next";

function Commitment() {
  const { t } = useTranslation();
  const {
    postIsLoading,
    ptwData,
    UsersList,
    setFileInputValues,
    fileInputValues,
    toast,
    setToast,
    formSubmitted,
    setInputValues,
    inputValues,
    handleDynamicChange,
    dynamicFields,
    handleSubmit,

    RiskRanking,
    fileNames,
    prev,
  } = MyFormData();

  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [stepInputDialogOpen, setStepInputDialogOpen] = useState(false);
  const [allRequiredFilled, setAllRequiredFilled] = useState(false);

  const validateDynamicFields = useCallback(() => {
    // Filter fields: get required fields + ALL CONFIRMATION fields (always required)
    const fieldsToValidate = dynamicFields.filter(
      (field) => field.required || field.input_type === "CONFIRMATION"
    );

    const filled = fieldsToValidate.every((field) => {
      const val = ptwData.stepInputsData?.[field.id];

      if (field.input_type === "BOOLEAN") {
        // Boolean must have a selection (YES or NO) if required
        return val === "YES" || val === "NO";
      }

      if (field.input_type === "CONFIRMATION") {
        // Confirmation must be checked (true) - ALWAYS required
        return val === true;
      }

      if (
        field.input_type === "MULTI_SELECTION" ||
        field.input_type === "MULTI_USER_SELECTION"
      ) {
        return Array.isArray(val) && val.length > 0;
      }

      if (field.input_type === "FILE_UPLOAD") {
        const files = fileInputValues?.[field.id];
        return Array.isArray(files) && files.length > 0;
      }

      return val !== undefined && val !== null && String(val).trim() !== "";
    });

    setAllRequiredFilled(filled);
  }, [ptwData.stepInputsData, fileInputValues, dynamicFields]);

  useEffect(() => {
    if (stepInputDialogOpen) {
      validateDynamicFields();
    }
  }, [stepInputDialogOpen, ptwData.stepInputsData, validateDynamicFields]);

  const handleFinalSubmit = (e) => {
    if (!allRequiredFilled) {
      setToast({
        open: true,
        message: t("commitment.errorDynamicFields"),
        severity: "error",
      });
      return;
    }
    handleSubmit(e);
    setStepInputDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          textAlign: "center",
          mb: 1,
          // Provide a minimum height so that on the last step the sticky stepper doesn't jump up
          minHeight: { xs: 400 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        {!formSubmitted && !postIsLoading ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("commitment.reviewMessage")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setSummaryDialogOpen(true)}
              aria-label={t("commitment.reviewButtonAria")}
            >
              {t("commitment.reviewButton")}
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                disabled={postIsLoading}
                variant="outlined"
                href="/newptw"
                aria-label={t("commitment.newPtwButtonAria")}
              >
                {t("commitment.newPtwButton")}
              </Button>
              <Button
                disabled={postIsLoading}
                variant="contained"
                href="/docs"
                aria-label={t("commitment.documentsButtonAria")}
              >
                {t("commitment.documentsButton")}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Summary Dialog */}
      <Dialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        scroll="paper"
        maxWidth="lg"
        fullWidth
        aria-labelledby="summary-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            minHeight: "60vh",
            maxHeight: "90vh",
            borderRadius: "16px",
            boxShadow: (theme) => theme.shadows[20],
          },
        }}
      >
        <DialogTitle
          id="summary-dialog-title"
          sx={{
            fontWeight: "bold",
            fontSize: (theme) => theme.typography.h4.fontSize,
            background: (theme) =>
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            color: (theme) => theme.palette.primary.contrastText,
            borderRadius: "16px 16px 0 0",
            py: 3,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: (theme) => theme.palette.primary.contrastText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
              }}
            >
              📋
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("commitment.summaryTitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontSize: "0.875rem",
                }}
              >
                {t("commitment.reviewMessage")}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label={t("commitment.closeDialogAria")}
            onClick={() => setSummaryDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: (theme) => theme.palette.primary.contrastText,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          }}
        >
          <WorkPermitSummary
            ptwData={ptwData}
            RiskRanking={RiskRanking || []}
            attachmentFileNames={fileNames || []}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.grey[50],
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={prev}
            variant="outlined"
            sx={{
              borderRadius: "25px",
              textTransform: "none",
              px: 3,
              py: 1,
            }}
          >
            {t("commitment.editButton")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSummaryDialogOpen(false);
              setStepInputDialogOpen(true);
            }}
            sx={{
              borderRadius: "25px",
              textTransform: "none",
              px: 4,
              py: 1,
              minWidth: 140,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              },
            }}
          >
            {t("commitment.proceedButton")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step Input Dialog */}
      <Dialog
        open={stepInputDialogOpen}
        onClose={() => setStepInputDialogOpen(false)}
        scroll="paper"
        maxWidth="lg"
        fullWidth
        aria-labelledby="step-input-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            minHeight: "60vh",
            maxHeight: "90vh",
            borderRadius: "16px",
            boxShadow: (theme) => theme.shadows[20],
          },
        }}
      >
        <DialogTitle
          id="step-input-dialog-title"
          sx={{
            fontWeight: "bold",
            fontSize: (theme) => theme.typography.h4.fontSize,
            background: (theme) =>
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            color: (theme) => theme.palette.primary.contrastText,
            borderRadius: "16px 16px 0 0",
            py: 3,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: (theme) => theme.palette.primary.contrastText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
              }}
            >
              📝
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("commitment.additionalFieldsTitle")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontSize: "0.875rem",
                }}
              >
                {t("commitment.fillAdditionalFields")}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label={t("commitment.closeDialogAria")}
            onClick={() => setStepInputDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: (theme) => theme.palette.primary.contrastText,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          }}
        >
          {dynamicFields && dynamicFields.length > 0 && (
            <StepInputsForm
              stepInputs={dynamicFields}
              values={ptwData.stepInputsData || {}}
              onChange={handleDynamicChange}
              userList={UsersList}
              inputValues={inputValues}
              setInputValues={setInputValues}
              fileInputValues={fileInputValues}
              setFileInputValues={setFileInputValues}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.grey[50],
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {allRequiredFilled ? (
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.success.main,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                ✅ {t("commitment.allFieldsComplete")}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.warning.main,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                ⚠️ {t("commitment.fieldsIncomplete")}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={() => {
                setStepInputDialogOpen(false);
                setSummaryDialogOpen(true);
              }}
              variant="outlined"
              sx={{
                borderRadius: "25px",
                textTransform: "none",
                px: 3,
                py: 1,
              }}
            >
              {t("commitment.backToSummary")}
            </Button>
            <Button
              variant="contained"
              onClick={handleFinalSubmit}
              disabled={!allRequiredFilled || postIsLoading}
              aria-label={t("commitment.finalSubmitButtonAria")}
              sx={{
                borderRadius: "25px",
                textTransform: "none",
                px: 4,
                py: 1,
                minWidth: 140,
                background: (theme) =>
                  !allRequiredFilled || postIsLoading
                    ? theme.palette.action.disabled
                    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                "&:hover": {
                  background: (theme) =>
                    !allRequiredFilled || postIsLoading
                      ? theme.palette.action.disabled
                      : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
              }}
            >
              {postIsLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">
                    {t("commitment.submitting")}
                  </Typography>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      border: "2px solid currentColor",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </Box>
              ) : (
                <>
                  {t("commitment.submitIcon")}{" "}
                  {t("commitment.finalSubmitButton")}
                </>
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Toast
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}

export default Commitment;
